import React, { useEffect, useRef, useState } from 'react';
import { X, Camera, Loader2, Image as ImageIcon } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { getMyProfile, updateMyProfile, uploadMyAvatar, MyProfile } from '../services/profileApi';

function fileToCompressedDataUrl(file: File, max = 512, quality = 0.85): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer la imagen'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Imagen inválida'));
      img.onload = () => {
        let { width, height } = img;
        if (width >= height && width > max) { height = Math.round(height * max / width); width = max; }
        else if (height > max) { width = Math.round(width * max / height); height = max; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('No se pudo procesar'));
        ctx.drawImage(img, 0, 0, width, height);
        // webp: ~30% más liviano que jpeg a igual calidad; el backend ya lo acepta/guarda/sirve.
        resolve(canvas.toDataURL('image/webp', quality));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const daysIn = (m: number) => (m ? new Date(2000, m, 0).getDate() : 31);
const initials = (n: string) => (n || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0] || '').join('').toUpperCase() || '?';

interface Props { open: boolean; onClose: () => void; onUpdated?: (p: MyProfile) => void; }

const MyProfileModal: React.FC<Props> = ({ open, onClose, onUpdated }) => {
  const toast = useToast();
  const [data, setData] = useState<MyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bMonth, setBMonth] = useState(0);
  const [bDay, setBDay] = useState(0);
  const [photoMenu, setPhotoMenu] = useState(false);
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getMyProfile()
      .then((r) => {
        setData(r.profile);
        if (r.profile.birthdate) {
          setBMonth(Number(String(r.profile.birthdate).slice(5, 7)) || 0);
          setBDay(Number(String(r.profile.birthdate).slice(8, 10)) || 0);
        }
      })
      .catch((e) => toast.error(e?.message || 'No se pudo cargar tu perfil'))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const set = (k: keyof MyProfile, v: any) => setData((d) => (d ? { ...d, [k]: v } : d));

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      const r = await uploadMyAvatar(dataUrl);
      set('avatar_url', r.avatar_url);
      if (data) onUpdated?.({ ...data, avatar_url: r.avatar_url });
      toast.success('Foto actualizada');
    } catch (err: any) {
      toast.error(err?.message || 'No se pudo subir la foto');
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    if (!data) return;
    setSaving(true);
    try {
      const birthdate = (bMonth && bDay)
        ? `2000-${String(bMonth).padStart(2, '0')}-${String(Math.min(bDay, daysIn(bMonth))).padStart(2, '0')}`
        : null;
      const r = await updateMyProfile({ name: data.name, phone: data.phone, birthdate });
      setData(r.profile);
      onUpdated?.(r.profile);
      toast.success('Perfil guardado');
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  }

  const inputCls = 'w-full bg-surface-2 text-content rounded-lg px-3 py-2 text-sm border border-line focus:outline-none focus:ring-2 focus:ring-accent/40';
  const labelCls = 'block text-[11px] font-medium uppercase tracking-wide text-content-3 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative w-full max-w-md bg-surface rounded-xl shadow-xl border border-line max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-line sticky top-0 bg-surface">
          <h2 className="text-base font-semibold text-content">Mi Perfil</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface-2 text-content-3"><X size={18} /></button>
        </div>

        {loading || !data ? (
          <div className="flex items-center justify-center py-16 text-content-3"><Loader2 className="animate-spin mr-2" size={20} />Cargando…</div>
        ) : (
          <div className="p-4">
            <div className="flex flex-col items-center mb-6">
              <div className="relative">
                <button onClick={() => setPhotoMenu((o) => !o)} disabled={uploading}
                  className="relative rounded-full active:scale-95 transition-transform disabled:opacity-60" aria-label="Cambiar foto">
                  {data.avatar_url
                    ? <img src={data.avatar_url} alt={data.name} className="w-24 h-24 rounded-full object-cover border-2 border-accent/40" />
                    : <span className="w-24 h-24 rounded-full bg-accent/15 text-accent-ink text-2xl font-bold flex items-center justify-center border-2 border-accent/40">{initials(data.name)}</span>}
                  <span className="absolute bottom-0 right-0 bg-accent text-on-accent rounded-full p-1.5 shadow">
                    {uploading ? <Loader2 className="animate-spin" size={14} /> : <Camera size={14} />}
                  </span>
                </button>
                {photoMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setPhotoMenu(false)} />
                    <div className="absolute z-20 left-1/2 -translate-x-1/2 top-full mt-2 w-48 bg-surface border border-line rounded-lg shadow-lg py-1">
                      <button onClick={() => { setPhotoMenu(false); cameraRef.current?.click(); }}
                        className="w-full text-left px-3 py-2 text-sm text-content-2 hover:bg-surface-2 flex items-center gap-2">
                        <Camera size={15} /> Tomar foto
                      </button>
                      <button onClick={() => { setPhotoMenu(false); galleryRef.current?.click(); }}
                        className="w-full text-left px-3 py-2 text-sm text-content-2 hover:bg-surface-2 flex items-center gap-2">
                        <ImageIcon size={15} /> Subir de galería
                      </button>
                    </div>
                  </>
                )}
              </div>
              <input ref={cameraRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onPick} />
              <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
              <p className="text-content-3 text-xs mt-2">Toca para tomar o subir una foto</p>
            </div>

            <div className="space-y-4">
              <div><label className={labelCls}>Nombre</label>
                <input className={inputCls} value={data.name || ''} onChange={(e) => set('name', e.target.value)} /></div>
              <div><label className={labelCls}>Teléfono</label>
                <input className={inputCls} value={data.phone || ''} onChange={(e) => set('phone', e.target.value)} placeholder="Opcional" /></div>
              <div><label className={labelCls}>Cumpleaños 🎂 <span className="normal-case tracking-normal text-content-3/70">(solo día y mes)</span></label>
                <div className="grid grid-cols-2 gap-3">
                  <select className={inputCls} value={bDay || ''} onChange={(e) => setBDay(Number(e.target.value))}>
                    <option value="">Día</option>
                    {Array.from({ length: daysIn(bMonth) }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select className={inputCls} value={bMonth || ''}
                    onChange={(e) => { const m = Number(e.target.value); setBMonth(m); if (bDay > daysIn(m)) setBDay(daysIn(m)); }}>
                    <option value="">Mes</option>
                    {MESES.map((mn, i) => <option key={i} value={i + 1}>{mn}</option>)}
                  </select>
                </div></div>
              {data.email && <div><label className={labelCls}>Correo</label>
                <input className={inputCls + ' opacity-60'} value={data.email} disabled /></div>}
            </div>

            <button onClick={save} disabled={saving}
              className="mt-6 w-full py-2.5 rounded-lg bg-accent text-on-accent font-semibold text-sm hover:opacity-90 active:scale-95 transition disabled:opacity-60">
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProfileModal;
